/**
 * SS Consent banner — <ss-consent-banner>
 * GDPR cookie consent with a signed record of every decision.
 *
 *  - Writes the decision to Shopify's Customer Privacy API
 *    (window.Shopify.customerPrivacy.setTrackingConsent) so Shopify analytics,
 *    pixels and apps obey it.
 *  - Updates Google Consent Mode v2 (gtag('consent','update',…)) and pushes
 *    `ss_consent_update` to the dataLayer.
 *  - Activates blocked scripts <script type="text/plain" data-ss-consent="analytics">.
 *  - Stores the record in localStorage['ss:consent'] and, when
 *    settings.ss_consent_log_endpoint is set, POSTs it (keepalive / sendBeacon):
 *    { id, timestamp, choices, method, policyVersion, bannerVersion, textHash,
 *      locale, country, pageType, shop, userAgentHint }  — no raw IP / UA.
 *  - Re-prompts when the record expired or the policy version changed.
 *  - Reopen anywhere with [data-ss-consent-open] or window.ssConsent.open().
 */
(function () {
  'use strict';
  if (customElements.get('ss-consent-banner')) return;

  var KEY = 'ss:consent';
  var CATS = ['analytics', 'marketing', 'preferences', 'sale_of_data'];

  function storage() { try { localStorage.setItem('__ss', '1'); localStorage.removeItem('__ss'); return localStorage; } catch (e) { return null; } }
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) { var r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16); });
  }
  function hash(str) { var h = 5381, i = str.length; while (i) h = (h * 33) ^ str.charCodeAt(--i); return (h >>> 0).toString(16); }
  function fmtDate(iso, locale) { try { return new Intl.DateTimeFormat(locale || undefined, { dateStyle: 'long', timeStyle: 'short' }).format(new Date(iso)); } catch (e) { return iso; } }

  class SsConsentBanner extends HTMLElement {
    constructor() { super(); this._l = []; }

    connectedCallback() {
      if (window.location.pathname === '/challenge') return;
      var cfgEl = this.querySelector('[data-ss-consent-config]');
      try { this.cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { this.cfg = null; }
      if (!this.cfg) return;
      if (this.cfg.strings) Object.keys(this.cfg.strings).forEach(function (k) {
        var v = this.cfg.strings[k];
        if (typeof v === 'string') this.cfg.strings[k] = v.replace(/&(#39|quot|lt|gt|amp);/g, function (m, e) { return { '#39': "'", quot: '"', lt: '<', gt: '>', amp: '&' }[e]; });
      }, this);
      this.store = storage();
      this.bar = this.querySelector('[data-ss-consent-bar]');
      this.modal = this.querySelector('[data-ss-consent-modal]');
      this.dialog = this.querySelector('[data-ss-consent-dialog]');
      this.gpcNote = this.querySelector('[data-ss-consent-gpc]');
      this.recordBox = this.querySelector('[data-ss-consent-record]');
      this.bannerText = (this.querySelector('[data-ss-consent-bartext]') || this).textContent.replace(/\s+/g, ' ').trim();
      this.toggles = {};
      this.querySelectorAll('[data-ss-consent-toggle]').forEach(function (t) { this.toggles[t.dataset.ssConsentToggle] = t; }, this);

      this.on(this, 'click', this.onClick.bind(this));
      this.on(document, 'click', function (e) { var o = e.target.closest && e.target.closest('[data-ss-consent-open]'); if (o) { e.preventDefault(); this.openModal(); } }.bind(this));
      this.on(document, 'ss:consent:request-open', this.openModal.bind(this));
      this.on(document, 'keydown', this.onKeydown.bind(this));
      this.exposeApi();

      if (this.cfg.designMode) {
        this.on(document, 'shopify:section:select', function (e) { if (e.detail && e.detail.sectionId === this.cfg.sectionId) this.showBar(); }.bind(this));
        this.on(document, 'shopify:section:deselect', function (e) { if (e.detail && e.detail.sectionId === this.cfg.sectionId) this.hideBar(); }.bind(this));
        if (this.cfg.mode === 'testing') this.showBar();
        return;
      }
      this.loadShopifyApi(this.evaluate.bind(this));
    }
    disconnectedCallback() { this._l.forEach(function (l) { l[0].removeEventListener(l[1], l[2]); }); this._l = []; }
    on(el, t, fn) { el.addEventListener(t, fn); this._l.push([el, t, fn]); }

    /* ---------- Shopify Customer Privacy API ---------- */
    loadShopifyApi(cb) {
      var done = false, finish = function () { if (!done) { done = true; cb(); } };
      try {
        if (window.Shopify && window.Shopify.customerPrivacy) return finish();
        if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], finish);
          setTimeout(finish, 2500);                      // never block the banner on the API
        } else finish();
      } catch (e) { finish(); }
    }
    get api() { return window.Shopify && window.Shopify.customerPrivacy; }

    /* ---------- decision ---------- */
    evaluate() {
      var rec = this.record();
      var gpc = this.cfg.honorGpc && navigator.globalPrivacyControl === true;
      if (rec && this.recordValid(rec)) { this.apply(rec, { silent: true }); return; }
      if (this.cfg.mode !== 'testing' && this.cfg.regionMode === 'shopify' && this.api && typeof this.api.shouldShowBanner === 'function' && !this.api.shouldShowBanner()) {
        /* Visitor outside the regions where Shopify requires consent: honour Shopify's state, no banner. */
        var granted = !!(this.api.userCanBeTracked && this.api.userCanBeTracked());
        this.apply(this.makeRecord(this.choicesAll(granted), 'not_required'), { silent: true, noLog: true });
        return;
      }
      if (gpc && this.gpcNote) this.gpcNote.hidden = false;
      setTimeout(this.showBar.bind(this), this.cfg.delay || 0);
    }
    record() { return this.store ? (function (s) { try { return JSON.parse(s.getItem(KEY) || 'null'); } catch (e) { return null; } })(this.store) : null; }
    recordValid(rec) {
      if (!rec || !rec.timestamp || !rec.choices) return false;
      if (rec.policyVersion !== this.cfg.policyVersion) return false;
      return (Date.now() - Date.parse(rec.timestamp)) < (this.cfg.expiryDays || 180) * 864e5;
    }
    choicesAll(v) { var c = {}; CATS.forEach(function (k) { c[k] = !!v; }); return c; }
    choicesFromToggles() { var c = {}; CATS.forEach(function (k) { c[k] = this.toggles[k] ? !!this.toggles[k].checked : false; }, this); return c; }
    makeRecord(choices, method) {
      return {
        id: uuid(), timestamp: new Date().toISOString(), choices: choices, method: method,
        policyVersion: this.cfg.policyVersion, bannerVersion: '1', textHash: hash(this.bannerText),
        locale: this.cfg.context.locale, country: this.cfg.context.country, pageType: this.cfg.context.pageType, shop: this.cfg.context.shop,
        userAgentHint: (navigator.userAgentData && navigator.userAgentData.platform) || (navigator.platform || '').slice(0, 20)
      };
    }

    /* ---------- apply + log ---------- */
    decide(choices, method) {
      var rec = this.makeRecord(choices, method);
      if (this.store) { try { this.store.setItem(KEY, JSON.stringify(rec)); } catch (e) {} }
      this.apply(rec, {});
      this.hideBar(); this.closeModal();
    }
    apply(rec, opts) {
      var c = rec.choices;
      /* 1. Shopify Customer Privacy API */
      try {
        if (this.api && typeof this.api.setTrackingConsent === 'function') {
          this.api.setTrackingConsent({ analytics: !!c.analytics, marketing: !!c.marketing, preferences: !!c.preferences, sale_of_data: !!c.sale_of_data }, function () {});
        }
      } catch (e) {}
      /* 2. Google Consent Mode v2 */
      if (this.cfg.consentMode && typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: c.analytics ? 'granted' : 'denied',
          ad_storage: c.marketing ? 'granted' : 'denied', ad_user_data: c.marketing ? 'granted' : 'denied', ad_personalization: c.marketing ? 'granted' : 'denied',
          functionality_storage: c.preferences ? 'granted' : 'denied', personalization_storage: c.preferences ? 'granted' : 'denied'
        });
      }
      /* 3. dataLayer + DOM event + queued callbacks */
      if (this.cfg.dataLayer) { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'ss_consent_update', consent_id: rec.id, consent_method: rec.method, consent: c }); }
      document.dispatchEvent(new CustomEvent('ss:consent:update', { detail: rec }));
      if (window.ssConsent) { window.ssConsent.ready = true; window.ssConsent.current = rec; (window.ssConsent._q || []).forEach(function (fn) { try { fn(rec); } catch (e) {} }); }
      /* 4. unblock scripts */
      this.activateScripts(c);
      /* 5. proof of consent */
      if (!opts.noLog && !opts.silent) this.log(rec);
      this.renderRecord(rec);
      CATS.forEach(function (k) { if (this.toggles[k]) this.toggles[k].checked = !!c[k]; }, this);
    }
    activateScripts(c) {
      document.querySelectorAll('script[type="text/plain"][data-ss-consent]').forEach(function (s) {
        var need = s.dataset.ssConsent.split(/[\s,]+/).filter(Boolean);
        if (!need.every(function (k) { return c[k]; })) return;
        var n = document.createElement('script');
        Array.prototype.forEach.call(s.attributes, function (a) { if (a.name !== 'type' && a.name !== 'data-src' && a.name !== 'data-ss-consent') n.setAttribute(a.name, a.value); });
        if (s.dataset.src) n.src = s.dataset.src; else n.textContent = s.textContent;
        n.setAttribute('data-ss-consent-activated', s.dataset.ssConsent);
        s.parentNode.replaceChild(n, s);
      });
    }
    log(rec) {
      var url = this.cfg.logEndpoint;
      if (!url) return;
      var body = JSON.stringify(rec);
      try {
        if (navigator.sendBeacon && !this.cfg.logApiKey) { navigator.sendBeacon(url, new Blob([body], { type: 'application/json' })); return; }
        var headers = { 'Content-Type': 'application/json' };
        if (this.cfg.logApiKey) headers['X-Api-Key'] = this.cfg.logApiKey;
        fetch(url, { method: 'POST', headers: headers, body: body, keepalive: true, mode: 'cors' }).catch(function () {});
      } catch (e) {}
    }
    renderRecord(rec) {
      if (!this.recordBox) return;
      this.recordBox.hidden = false;
      var g = this.recordBox.querySelector('[data-ss-consent-given]'), id = this.recordBox.querySelector('[data-ss-consent-id]');
      if (g) g.textContent = (this.cfg.strings.givenOn || '%DATE%').replace('%DATE%', fmtDate(rec.timestamp, this.cfg.context.locale + '-' + this.cfg.context.country));
      if (id) id.textContent = rec.id;
    }

    /* ---------- UI ---------- */
    showBar() { if (!this.bar) return; this.removeAttribute('hidden'); this.bar.hidden = false; void this.bar.offsetWidth; this.classList.add('is-bar-open'); }
    hideBar() { if (!this.bar) return; this.classList.remove('is-bar-open'); this.bar.hidden = true; if (this.modal && this.modal.hidden) this.setAttribute('hidden', ''); }
    openModal() {
      if (!this.modal) return;
      var rec = this.record(); if (rec && rec.choices) CATS.forEach(function (k) { if (this.toggles[k]) this.toggles[k].checked = !!rec.choices[k]; }, this);
      if (rec && rec.choices) this.renderRecord(rec);
      this.lastFocus = document.activeElement;
      this.removeAttribute('hidden'); this.modal.hidden = false; document.body.classList.add('ss-consent-modal-open');
      setTimeout(function () { this.dialog.focus({ preventScroll: true }); }.bind(this), 30);
    }
    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true; document.body.classList.remove('ss-consent-modal-open');
      if (this.bar && this.bar.hidden) this.setAttribute('hidden', '');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }
    onClick(e) {
      var btn = e.target.closest('[data-ss-consent-action]'); if (!btn || !this.contains(btn)) return;
      switch (btn.dataset.ssConsentAction) {
        case 'accept_all': this.decide(this.choicesAll(true), 'accept_all'); break;
        case 'reject_all': this.decide(this.choicesAll(false), 'reject_all'); break;
        case 'save': this.decide(this.choicesFromToggles(), 'custom'); break;
        case 'withdraw': this.decide(this.choicesAll(false), 'withdraw'); break;
        case 'manage': this.openModal(); break;
        case 'close-modal': this.closeModal(); if (!this.record()) this.showBar(); break;
      }
    }
    onKeydown(e) {
      if (!this.modal || this.modal.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); this.closeModal(); if (!this.record()) this.showBar(); return; }
      if (e.key !== 'Tab') return;
      var f = Array.prototype.filter.call(this.dialog.querySelectorAll('button, input, summary, [href], [tabindex]:not([tabindex="-1"])'), function (el) { return !el.disabled && el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    exposeApi() {
      var self = this; window.ssConsent = window.ssConsent || { _q: [] };
      window.ssConsent.open = function () { self.openModal(); };
      window.ssConsent.get = function () { return self.record(); };
      window.ssConsent.has = function (cat) { var r = self.record(); return !!(r && r.choices && r.choices[cat]); };
      window.ssConsent.on = function (fn) { var r = self.record(); if (r && self.recordValid(r)) { try { fn(r); } catch (e) {} } else (window.ssConsent._q = window.ssConsent._q || []).push(fn); };
      window.ssConsent.withdraw = function () { self.decide(self.choicesAll(false), 'withdraw'); };
    }
  }
  customElements.define('ss-consent-banner', SsConsentBanner);
})();
